<template>
  <div class="p-5">
    <VChart
      key="workforce-forecast-chart-v2"
      :option="chartOption"
      :notMerge="true"
      autoresize
      style="width: 100%; height: 380px"
    />
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

const props = defineProps({
  months: { type: Array, required: true },
  series: { type: Object, required: true },
  range: { type: Object, default: null },
  transitions: { type: Array, default: () => [] }
})

const isDarkMode = ref(false)

const updateDarkMode = () => {
  isDarkMode.value = document.documentElement.classList.contains('dark')
}

let observer = null

onMounted(() => {
  updateDarkMode()
  observer = new MutationObserver(updateDarkMode)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const SERIES_DEFINITIONS = [
  { key: 'supply_fte', name: 'Beschikbare capaciteit', color: '#10b981' },
  { key: 'committed_demand_fte', name: 'Lopende projecten', color: '#3b82f6' },
  {
    key: 'current_expected_demand_fte',
    name: 'Verwachte vraag',
    color: '#8b5cf6'
  },
  {
    key: 'historical_demand_fte',
    name: 'Historische vraag',
    color: '#94a3b8',
    dashed: true
  }
]

// Plain-language explanations shown when hovering a legend item. Each series
// explains what it represents and how it is derived from the forecast data.
const LEGEND_DESCRIPTIONS = {
  'Beschikbare capaciteit': 'Verwachte inzetbare FTE per maand uit de personeelsplanning.',
  'Lopende projecten': 'Geplande PM-uren uit Simplicate plus de actieve pre-sourcing opdrachten.',
  'Verwachte vraag': 'Som van de lopende projecten en de kansgewogen salesfunnel per maand.',
  'Historische vraag':
    'Projectmanagementuren uit dezelfde kalendermaand een jaar eerder; als er voor die maand geen historie is, is de reeks nul.',
  'Sluitingskans (laag–hoog)':
    'Bandbreedte van de verwachte vraag bij een lage tot hoge sluitingskans van open saleskansen.'
}

function formatMonthLabel(monthKey) {
  const [year, month] = monthKey.split('-')
  const monthNames = [
    'jan',
    'feb',
    'mrt',
    'apr',
    'mei',
    'jun',
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'dec'
  ]
  return `${monthNames[parseInt(month, 10) - 1]} '${year.slice(2)}`
}

function round(value) {
  return Math.round((Number(value) || 0) * 100) / 100
}

function transitionLabel(transition) {
  const count = Number(transition.employee_count) || 0
  return `${count} ${transition.from_level} → ${transition.to_level}`
}

const CLOSING_RANGE_NAME = 'Sluitingskans (laag–hoog)'

const chartOption = computed(() => {
  const dark = isDarkMode.value
  const colors = {
    textPrimary: dark ? '#e5e7eb' : '#374151',
    textSecondary: dark ? '#9ca3af' : '#6b7280',
    border: dark ? '#4b5563' : '#e5e7eb',
    tooltipBg: dark ? '#1f2937' : '#ffffff'
  }

  const labels = props.months.map(formatMonthLabel)
  const transitionLines = props.transitions
    .filter(transition => props.months.includes(transition.month))
    .map(transition => ({
      xAxis: formatMonthLabel(transition.month),
      name: transitionLabel(transition)
    }))

  const hasClosingRange = Boolean(props.range?.low && props.range?.high)
  const closingRangeBaseData = props.months.map(month => {
    const low = Number(props.range?.low?.[month]) || 0
    const high = Number(props.range?.high?.[month]) || 0
    return round(Math.min(low, high))
  })
  const closingRangeHeightData = props.months.map(month => {
    const low = Number(props.range?.low?.[month]) || 0
    const high = Number(props.range?.high?.[month]) || 0
    return round(Math.max(high - low, 0))
  })
  const hiddenAreaBase = (name, data, stack) => ({
    name,
    type: 'line',
    stack,
    data,
    showSymbol: false,
    silent: true,
    tooltip: { show: false },
    lineStyle: { color: 'transparent', width: 0, opacity: 0 },
    itemStyle: { color: 'transparent', opacity: 0 },
    areaStyle: { color: 'transparent', opacity: 0 },
    z: 1
  })
  const filledArea = (name, data, stack, color, opacity) => ({
    name,
    type: 'line',
    stack,
    data,
    showSymbol: false,
    silent: true,
    tooltip: { show: false },
    lineStyle: { color: 'transparent', width: 0, opacity: 0 },
    itemStyle: { color, opacity: 0 },
    areaStyle: { color, opacity },
    z: 1
  })
  const areaSeries = [
    ...(hasClosingRange
      ? [
          hiddenAreaBase('__closing_range_base', closingRangeBaseData, 'closing-range'),
          filledArea(CLOSING_RANGE_NAME, closingRangeHeightData, 'closing-range', '#8b5cf6', 0.2)
        ]
      : [])
  ]
  const lineSeries = SERIES_DEFINITIONS.map(definition => ({
    name: definition.name,
    type: 'line',
    smooth: true,
    symbol: 'circle',
    symbolSize: 5,
    data: props.months.map(month => round(props.series?.[definition.key]?.[month] ?? 0)),
    lineStyle: {
      width: definition.key === 'current_expected_demand_fte' ? 3 : 2,
      color: definition.color,
      type: definition.dashed ? 'dashed' : 'solid'
    },
    itemStyle: { color: definition.color },
    z: 3,
    ...(definition.key === 'current_expected_demand_fte' && transitionLines.length > 0
      ? {
          markLine: {
            silent: true,
            symbol: ['none', 'none'],
            label: {
              color: '#0f766e',
              fontSize: 10,
              formatter: params => params.name,
              position: 'insideStartTop'
            },
            lineStyle: { color: '#14b8a6', type: 'dotted', width: 1.5 },
            data: transitionLines
          }
        }
      : {})
  }))
  const series = [...areaSeries, ...lineSeries]
  const legendData = [
    ...SERIES_DEFINITIONS.map(definition => definition.name),
    ...(hasClosingRange ? [CLOSING_RANGE_NAME] : [])
  ]

  return {
    animation: true,
    grid: { left: 55, right: 24, top: 60, bottom: 40 },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.border,
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      valueFormatter: value => `${Number(value).toFixed(2)} FTE`
    },
    legend: {
      data: legendData,
      top: 0,
      type: 'scroll',
      textStyle: { color: colors.textPrimary, fontSize: 11 },
      tooltip: {
        show: true,
        formatter: ({ name }) => LEGEND_DESCRIPTIONS[name] ?? name
      }
    },
    xAxis: {
      type: 'category',
      data: labels,
      boundaryGap: false,
      axisLabel: { color: colors.textPrimary, fontSize: 12 },
      axisLine: { lineStyle: { color: colors.border } },
      axisTick: { lineStyle: { color: colors.border } }
    },
    yAxis: {
      type: 'value',
      name: 'FTE',
      nameTextStyle: { color: colors.textSecondary, fontSize: 11 },
      axisLabel: { color: colors.textSecondary, fontSize: 11 },
      axisLine: { lineStyle: { color: colors.border } },
      splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
    },
    series
  }
})
</script>
